import type { RefObject } from "react";
import {
  REJECTION_CANNED_REASONS,
  CANNED_REASON_TOO_FULL_MESSAGE,
  CANNED_REASON_TITLE_PREFIX,
} from "../../../constants/validation";
import { appendCannedReason } from "../../../utils/text.utils";

/**
 * Color palette for the buttons. `gray` matches the red-accented bulk reject
 * modal; `neutral` matches the primary-accented detail views.
 */
type CannedReasonVariant = "gray" | "neutral";

interface CannedReasonButtonsProps {
  /** Current value of the rejection reason textarea. */
  value: string;
  /** Called with the new value when a canned reason is appended. */
  onAppend: (next: string) => void;
  /** Maximum allowed character count (inclusive). */
  maxLength: number;
  /** When true, all buttons are disabled (e.g. request in flight). */
  disabled?: boolean;
  /**
   * Optional ref to the textarea. When provided, after appending the textarea
   * is focused, the caret moved to the end and scrolled into view.
   */
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
  /** Color palette. Defaults to `neutral`. */
  variant?: CannedReasonVariant;
}

const VARIANT_CLASSES: Record<CannedReasonVariant, string> = {
  gray: "text-gray-600 border-gray-300 hover:bg-gray-50 focus:ring-red-500",
  neutral:
    "text-neutral-600 border-neutral-300 hover:bg-neutral-50 focus:ring-primary-500",
};

/**
 * Canned rejection reason helper buttons. Renders one button per
 * {@link REJECTION_CANNED_REASONS} entry; clicking appends the snippet to the
 * current value (via {@link appendCannedReason}) and reports it through
 * `onAppend`. A button is disabled when appending would exceed `maxLength`.
 *
 * Shared by BulkRejectModal, RecommendationDetailsModal and the
 * GrantsRecommendationDetails page so rejection helpers behave identically
 * everywhere a moderator can reject.
 */
export function CannedReasonButtons({
  value,
  onAppend,
  maxLength,
  disabled = false,
  textareaRef,
  variant = "neutral",
}: CannedReasonButtonsProps) {
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {REJECTION_CANNED_REASONS.map((reason) => {
        const next = appendCannedReason(value, reason.text, maxLength);
        const wouldExceed = next === value && value !== reason.text;
        const isDisabled = disabled || wouldExceed;
        return (
          <button
            key={reason.label}
            type="button"
            disabled={isDisabled}
            aria-label={
              wouldExceed
                ? `Nie można dopisać: ${reason.text} — pole jest zbyt pełne`
                : `${CANNED_REASON_TITLE_PREFIX} ${reason.text}`
            }
            title={
              wouldExceed
                ? CANNED_REASON_TOO_FULL_MESSAGE
                : `${CANNED_REASON_TITLE_PREFIX} ${reason.text}`
            }
            onClick={() => {
              if (next === value) return;
              onAppend(next);
              // Focus textarea, place cursor at end, scroll into view
              setTimeout(() => {
                const el = textareaRef?.current;
                if (!el) return;
                el.focus();
                el.setSelectionRange(next.length, next.length);
                el.scrollIntoView({ block: "nearest" });
              }, 0);
            }}
            className={`px-2.5 py-1 text-xs font-medium bg-white border rounded-md focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${VARIANT_CLASSES[variant]}`}
          >
            {reason.label}
          </button>
        );
      })}
    </div>
  );
}
